import { useEffect, useState } from "react"
import { HocuspocusProvider } from "@hocuspocus/provider"
import * as Y from "yjs"

/**
 * React hook for real-time user presence tracking across multiple browser tabs.
 *
 * This hook establishes a WebSocket connection to track users currently active
 * within the same account, including which pages they're viewing and their tab status.
 * It automatically handles tab lifecycle events (focus, blur, close) and merges
 * presence data from multiple tabs per user.
 *
 * @param {Object} user - Current user object with id, name, email, avatar_url, account_id
 * @returns {Object} Presence state and methods
 * @returns {Array} returns.onlineUsers - Array of online users with their tab information
 * @returns {string} returns.tabId - Unique identifier for current browser tab
 * @returns {HocuspocusProvider|null} returns.provider - WebSocket provider instance
 *
 * @example
 * const { onlineUsers } = usePresence(currentUser)
 *
 * // onlineUsers structure:
 * [
 *   {
 *     id: 123,
 *     name: "John Doe",
 *     email: "john@example.com",
 *     avatar: "https://example.com/avatar.jpg",
 *     accountId: 456,
 *     tabs: {
 *       "tab-123-abc": {
 *         page: "/dashboard",
 *         timestamp: 1640995200000,
 *         status: "active",
 *         lastSeen: 1640995200000,
 *         title: "Dashboard - ConvertKit",
 *         isActive: true
 *       }
 *     }
 *   }
 * ]
 */
export function usePresence(user) {
  const [provider, setProvider] = useState(null)
  const [onlineUsers, setOnlineUsers] = useState([])
  const [tabId] = useState(() => `tab-${Date.now()}-${Math.random()}`)
  const accountId = user?.account_id

  useEffect(() => {
    if (!user || !user.account_id) return

    const setupPresence = async () => {
      try {
        const doc = new Y.Doc()
        const presenceProvider = new HocuspocusProvider({
          url: `${process.env.NODE_FUNCTIONS_WEBSOCKET_HOST}/collaboration`,
          name: `presence:account-${user.account_id}`,
          document: doc
        })

        const cleanupFunctions = setupPresenceProvider(
          presenceProvider,
          user,
          tabId,
          setOnlineUsers,
          setProvider
        )

        return { presenceProvider, cleanupFunctions }
      } catch (error) {
        // TODO: send to bugsnag?
        console.error("Failed to setup websocket presence:", error)
      }
    }

    const { presenceProvider, cleanupFunctions } = setupPresence()

    return () => {
      if (presenceProvider) {
        cleanupPresenceProvider(presenceProvider, tabId, cleanupFunctions)
      }
    }
  }, [user, accountId, tabId])

  return { onlineUsers, provider, tabId }
}

function setupPresenceProvider(
  presenceProvider,
  user,
  tabId,
  setOnlineUsers,
  setProvider
) {
  // Set user info (same across all tabs)
  presenceProvider.awareness.setLocalStateField("user", {
    id: user.id,
    name: user.name,
    email: user.email,
    avatar: user.avatar_url,
    accountId: user.account_id
  })

  // Set tab-specific activity
  presenceProvider.awareness.setLocalStateField("tabs", {
    [tabId]: {
      page: window.location.pathname,
      timestamp: Date.now(),
      status: "active",
      lastSeen: Date.now(),
      title: document.title,
      isActive: !document.hidden // Track if tab is focused
    }
  })

  // Listen for presence changes and merge tabs per user
  const updateOnlineUsers = () => {
    const states = Array.from(presenceProvider.awareness.getStates().values())

    // Group by user ID to merge multiple tabs
    const userMap = new Map()

    states.forEach(state => {
      if (!state.user || !state.tabs) return

      const userId = state.user.id
      if (!userMap.has(userId)) {
        userMap.set(userId, {
          ...state.user,
          tabs: {}
        })
      }

      // Merge tabs from this client
      const existingUser = userMap.get(userId)
      existingUser.tabs = { ...existingUser.tabs, ...state.tabs }
    })

    setOnlineUsers(Array.from(userMap.values()))
  }

  presenceProvider.awareness.on("change", updateOnlineUsers)
  updateOnlineUsers()

  setProvider(presenceProvider)

  // Track tab visibility changes
  const handleVisibilityChange = () => {
    const currentTabs = presenceProvider.awareness.getLocalState()?.tabs || {}
    presenceProvider.awareness.setLocalStateField("tabs", {
      ...currentTabs,
      [tabId]: {
        ...currentTabs[tabId],
        isActive: !document.hidden,
        lastSeen: Date.now()
      }
    })
  }

  document.addEventListener("visibilitychange", handleVisibilityChange)

  // Cleanup tab when closing
  const handleBeforeUnload = () => {
    const currentTabs = presenceProvider.awareness.getLocalState()?.tabs || {}
    delete currentTabs[tabId]
    presenceProvider.awareness.setLocalStateField("tabs", currentTabs)
  }

  window.addEventListener("beforeunload", handleBeforeUnload)

  // Store cleanup functions for later use
  return {
    updateOnlineUsers,
    handleVisibilityChange,
    handleBeforeUnload
  }
}

function cleanupPresenceProvider(presenceProvider, tabId, cleanupFunctions) {
  if (cleanupFunctions) {
    presenceProvider.awareness.off("change", cleanupFunctions.updateOnlineUsers)
    document.removeEventListener(
      "visibilitychange",
      cleanupFunctions.handleVisibilityChange
    )
    window.removeEventListener(
      "beforeunload",
      cleanupFunctions.handleBeforeUnload
    )
  }

  // Clean up this tab
  const currentTabs = presenceProvider.awareness.getLocalState()?.tabs || {}
  delete currentTabs[tabId]
  presenceProvider.awareness.setLocalStateField("tabs", currentTabs)
  presenceProvider.destroy()
}
